import * as React from 'react';
import { ReactPlugin, withAITracking } from '@microsoft/applicationinsights-react-js';
import type {
  ICustomProperties,
  IEventTelemetry,
  IMetricTelemetry,
  IPageViewTelemetry,
} from '@microsoft/applicationinsights-web';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import type { History } from 'history';
import { useHistory } from 'react-router-dom';
import type { User } from '@foundershub/api-client';
import { useKeyStore } from '../key-store';
import { requestIdleCallback } from '../request-idle-callback';
import { addScript } from '../scripts';

declare global {
  /* eslint-disable no-var */
  var __ai__: ApplicationInsights | undefined;
  var __rp__: ReactPlugin | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  var __1dsai__: any;
  var __REVISION__: string;
  var GPC_DataSharingOptIn: boolean;
  /* eslint-enable no-var */
}

interface Opts {
  // Connection string for Azure App Insights
  connectionString?: string;
  // Instrumentation key for 1DS App Insights
  instrumentationKey?: string;
  withTracking?: typeof withAITracking;
  history: History;
}

export function withTelemetry<P>(
  app: React.ComponentType<P>,
  { connectionString, instrumentationKey, withTracking = withAITracking, history }: Opts
) {
  if (!__APP_TELEMETRY_ENABLED__ || !connectionString) {
    return app;
  }

  if (!global.__ai__ || !global.__rp__) {
    global.__rp__ = new ReactPlugin();
    global.__ai__ = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        extensions: [global.__rp__],
        extensionConfig: {
          [global.__rp__.identifier]: { history },
        },
      },
    });

    global.__ai__.loadAppInsights();
    global.__ai__.context.application.ver = __REVISION__ || 'unknown';
  }

  if (!global.__1dsai__ && instrumentationKey) {
    requestIdleCallback(() => {
      addScript({
        src: 'https://js.monitor.azure.com/scripts/c/ms.analytics-web-3.min.js',
        onload: () => {
          try {
            // @ts-ignore set by an external script
            global.__1dsai__ = new window.oneDS.ApplicationInsights();
            global.__1dsai__.initialize({
              instrumentationKey,
              propertyConfiguration: {
                gpcDataSharingOptIn: window.GPC_DataSharingOptIn, // Include the CCPA "GPC_DataSharingOptIn" property
              },
              webAnalyticsConfiguration: {
                urlCollectQuery: true,
                autoCapture: {
                  scroll: true,
                  pageView: true,
                  onLoad: true,
                  onUnload: true,
                  click: true,
                  resize: true,
                  jsError: false,
                },
              },
              enableAutoRouteTracking: true,
              extensions: [global.__rp__],
            });
          } catch (e) {
            // pass
          }
        },
      });
    });
  }

  return withTracking<P>(global.__rp__, app);
}

export function trackEvent(event: IEventTelemetry, customProperties?: ICustomProperties) {
  if (!__APP_TELEMETRY_ENABLED__ || !global.__ai__) {
    return;
  }

  return global.__ai__.trackEvent(event, customProperties);
}

export function trackMetric(metric: IMetricTelemetry, customProperties?: ICustomProperties) {
  if (!__APP_TELEMETRY_ENABLED__ || !global.__ai__) {
    return;
  }

  return global.__ai__.trackMetric(metric, customProperties);
}

export function trackException(error: Error) {
  if (!__APP_TELEMETRY_ENABLED__ || !global.__ai__) {
    return;
  }

  return global.__ai__.trackException({ exception: error });
}

export function trackPageView(pageTelemetry?: IPageViewTelemetry) {
  if (!global.__ai__ || !global.__1dsai__) {
    return;
  }

  return global.__1dsai__.trackPageView(pageTelemetry) && global.__ai__.trackPageView();
}

export function setUser(user: User) {
  if (!__APP_TELEMETRY_ENABLED__ || !global.__ai__) {
    return;
  }

  return global.__ai__.setAuthenticatedUserContext(user.email);
}

export function unsetUser() {
  if (!__APP_TELEMETRY_ENABLED__ || !global.__ai__) {
    return;
  }

  return global.__ai__.clearAuthenticatedUserContext();
}

export function __testReset() {
  // @ts-ignore
  global.__ai__ = undefined;
  // @ts-ignore
  global.__rp__ = undefined;
  // @ts-ignore
  global.__1dsai__ = undefined;
}

export function AppWrapper({ app: UnwrappedApp }: { app: React.ComponentType }) {
  const { appInsightsConnectionString, oneDSAppInsightsInstrumentationKey } = useKeyStore();
  const history = useHistory();
  const App = withTelemetry(UnwrappedApp, {
    connectionString: appInsightsConnectionString,
    instrumentationKey: oneDSAppInsightsInstrumentationKey,
    history,
  });
  return <App />;
}
